import { useCallback } from 'react'
import {
  StorageItemFragment,
  useTransferStorageItemsMutation,
  TransferStorageItemsMutation,
  TransferStorageItemsMutationVariables,
} from '@sketch/gql-types'
import { castError, useEventDispatch } from '@sketch/utils'
import { MutationFunctionOptions } from '@apollo/client'
import { updateCacheAfterMultipleStorageItemsTransferred } from './updateCacheAfterStorageItemTransferred'

// eslint-disable-next-line no-restricted-imports
import { MinimalStorageItem } from '@sketch/gql-types/expansive'
import { StorageItemIdentity } from '../../types'

const STORAGE_ITEM_PER_TYPENAME: Record<
  StorageItemFragment['__typename'],
  MinimalStorageItem['type']
> = {
  Share: 'SHARE',
  Project: 'PROJECT',
}

export interface StorageItemTransferredEvent {
  location: StorageItemTransferLocation
  items: StorageItemIdentity[]
}

declare module '@sketch/utils' {
  export interface EventsMap {
    storageItemTransferred: StorageItemTransferredEvent
  }
}

export interface StorageItemLocation {
  projectId?: string
  workspaceId: string
}

export interface StorageItemTransferLocation {
  source: StorageItemLocation
  destination: StorageItemLocation
}

export interface UseTransferStorageItemsProps {
  onError: (error: string) => void
}

export interface TransferStorageItemArgs
  extends OmitSafe<
    MutationFunctionOptions<
      TransferStorageItemsMutation,
      TransferStorageItemsMutationVariables
    >,
    'variables' | 'onCompleted'
  > {
  onCompleted?: (
    data: TransferStorageItemsMutation,
    location: StorageItemTransferLocation
  ) => void
  location: StorageItemTransferLocation
  items: Pick<StorageItemFragment, '__typename' | 'identifier'>[]
}

export function useTransferStorageItems(props: UseTransferStorageItemsProps) {
  const { onError } = props

  const [mutateFn, result] = useTransferStorageItemsMutation({
    onError: () => {
      // do nothing, as we handle errors in the `mutate` function
    },
  })

  const dispatchItemTransferred = useEventDispatch('storageItemTransferred')

  const mutate = useCallback(
    async (args: TransferStorageItemArgs) => {
      try {
        const { onCompleted, update, location, items, ...rest } = args
        const result = await mutateFn({
          ...rest,
          variables: {
            storageItems: items.map(({ __typename, identifier }) => ({
              identifier,
              type: STORAGE_ITEM_PER_TYPENAME[__typename],
            })),
            parentProjectIdentifier: location.destination.projectId,
            workspaceIdentifier: location.destination.workspaceId,
          },
          update: (cache, mutationResult, options) => {
            if (
              mutationResult.data &&
              (mutationResult.errors?.length || 0) === 0
            ) {
              const items =
                mutationResult.data.transferStorageItems.storageItems

              updateCacheAfterMultipleStorageItemsTransferred({
                cache,
                location,
                items,
              })
            }
            update?.(cache, mutationResult, options)
          },
        })

        const errorMessage =
          result.errors?.map(error => error.message).join(', ') ||
          result.error?.message

        if (errorMessage || !result.data) {
          onError(errorMessage || 'An error occurred')
          return
        }

        onCompleted && onCompleted(result.data, location)
        dispatchItemTransferred({ location, items })
      } catch (error) {
        const message = castError(error).message
        onError(message || 'An error occurred')
      }
    },
    [dispatchItemTransferred, mutateFn, onError]
  )

  return [mutate, result] as const
}
